import { OnInit, Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import { combineLatest, concat, defer, from, Observable, Subscription } from 'rxjs';
import { catchError, last, map, startWith, tap } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { CoreApiService } from '../../core-api.service';
import { DialogService } from '../../dialogs';
import { FileVersionDialog } from '../file-version-dialog/file-version-dialog.component';
import { InfoEditComponent } from '../info-edit/info-edit.component';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { getOriginAction } from '../../shared/file.utils';
import { validFileName } from '../../shared/form.utils';
import { Container } from '../../shared/models/container.model';
import { File, FileOrigin } from '../../shared/models/file.model';
import { Modality } from '../../shared/models/modality.model';
import { UserSettings } from '../../shared/models/user-settings.model';
import { TranslationBundle, TranslationService } from '../../translation.service';

export interface FileInfoDialogData {
  file: File,
  container: Container,
  writable?: boolean
}

export interface FileInfoDialogResult {
  dirty: boolean
}

interface ClassificationDimension {
  dimension: string
  options: SelectOption[]
}

interface SelectOption {
  value: string
  label?: string
}

@Component({
  selector: 'file-info-dialog',
  templateUrl: './file-info-dialog.component.html',
  styleUrls: ['./file-info-dialog.component.scss'],
  host: {
    class: 'mat-dialog-component',
  },
})
export class FileInfoDialog implements OnDestroy, OnInit {
  @ViewChild('customInfo', { static: true }) customInfo: InfoEditComponent;

  canRenameFile = false
  classificationDimensions: ClassificationDimension[] = []
  container = this.data.container
  file = this.data.file
  fileTypes: string[] = []
  readonly form = new FormGroup({
    name: new FormControl(this.file.name, [Validators.required, validFileName]),
    mimetype: new FormControl(this.file.mimetype, [/* Validators.required, validMimeType */]),
    modality: new FormControl(this.file.modality || ''),
    classification: new FormGroup({} as Record<string, FormControl<string[]>>),
    custom: new FormControl(this.customClassification),
    tags: new FormControl(this.file.tags),
    type: new FormControl(this.file.type),
  })
  modalityOptions: SelectOption[] = []
  permissions = {
    changeType: false,
    modifyClassification: false,
    modifyInfo: false,
    modifyTags: false,
    renameFiles: false,
  }
  writable = this.data.writable !== false

  private messages = {
    dialog: TranslationBundle.EMPTY,
    fileOriginActions: TranslationBundle.EMPTY,
  }
  private modalities = new Map<string, Modality>()
  private settings: UserSettings
  private subscriptions = {
    fileTypes: Subscription.EMPTY,
    messages: Subscription.EMPTY,
    permissions: Subscription.EMPTY,
    settings: Subscription.EMPTY,
  }

  constructor(
    private config: ConfigService,
    @Inject(MAT_DIALOG_DATA) private data: FileInfoDialogData,
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
    private ref: MatDialogRef<FileInfoDialog, FileInfoDialogResult>,
    private translate: TranslationService,
  ) { }

  ngOnInit(): void {
    this.dialog.registerDialog(FileInfoDialog, { multi: 'replace' });

    if (this.writable && this.file.parents?.project) {
      this.subscriptions.permissions = this.projectsService.getProjectPermissions(this.file.parents.project).subscribe(permissions => {
        this.permissions = {
          changeType: permissions.includes(PermissionAction.FilesModifyMetadata),
          modifyClassification: permissions.includes(PermissionAction.FilesModifyMetadata),
          modifyInfo: permissions.includes(PermissionAction.FilesModifyMetadata),
          modifyTags: permissions.includes(PermissionAction.TagsManage),
          renameFiles: permissions.includes(PermissionAction.FilesMove),
        };
      });
    }

    this.subscriptions.messages = combineLatest([
      this.translate.getBundle('fileInfoDialog'),
      this.translate.getBundle('fileOriginActions'),
    ]).subscribe(([dialog, fileOriginActions]) => {
      this.messages = { dialog, fileOriginActions };
    });

    if (this.writable) {
      this.form.get('modality').valueChanges.subscribe(value => {
        const modality: Modality = this.modalities.get(value) || { _id: value, description: '', classification: {} };
        this.setModalityClassifications(modality);
      });
      this.subscriptions.fileTypes = this.config.getFileTypes().subscribe(fileTypes => {
        this.fileTypes = sortBy(Object.keys(fileTypes), fileType => fileType.toLowerCase());
      });
    }

    this.coreApi.modalities.get().subscribe(modalities => {
      this.modalities = new Map(modalities.map(modality => [modality._id, modality]));
      if (this.writable) {
        const options: SelectOption[] = sortBy(Array.from(this.modalities.values()), modality =>
          (modality._id || '').toLowerCase()
        ).map(modality => (
          { value: modality._id, label: this.getModalityLabel(modality._id) }
        ));

        if (this.file.modality && !this.modalities.has(this.file.modality)) {
          options.unshift({ value: this.file.modality });
        }
        this.modalityOptions = [{ value: '' }].concat(options);

        // set classifications that are on the file even if they don't align with those associated with the modality
        const classification = this.form.get('classification') as FormGroup;
        for (const [dimensionKey, dimensionValue] of Object.entries(omit(this.file.classification, 'Custom'))) {
          if (!(dimensionKey in classification.controls)) {
            classification.addControl(dimensionKey, new FormControl(dimensionValue));
          }
        }

        // set classification controls according to the modality definition
        const fileModality = this.modalities.get(this.file.modality);
        if (fileModality) {
          this.setModalityClassifications(fileModality);
        }
      }
    });

    this.subscriptions.settings = this.profileService.settings$.subscribe(settings => {
      this.settings = settings;
    });

    if (this.data.container.container_type === ContainerType.Analysis) {
      this.coreApi.analyses.get(this.data.container._id).subscribe((analysis) => {
        for (const file of analysis.files) {
          // if current file is in analysis output canRenameFile should be left false
          if (file.file_id === this.data.file.file_id) {
            return;
          }
        }
        this.canRenameFile = true;
      });
    } else {
      this.canRenameFile = true;
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get hasModifiableFields(): boolean {
    // does the parent allow writing and do we have permission to modify something
    return this.writable && Object.values(this.permissions).some(permission => permission);
  }

  get dirty(): boolean {
    if (!this.hasModifiableFields) {
      return false;
    }
    return this.form.dirty || this.customInfo.dirty;
  }

  get valid(): boolean {
    return this.form.valid && !this.customInfo.invalid;
  }

  get originAction(): string {
    const action = getOriginAction(this.file);
    return this.messages.fileOriginActions.get(action);
  }

  get origin(): FileOrigin {
    return this.file.restored_by ?? this.file.origin;
  }

  get customClassification(): string {
    const custom = this.file.classification['Custom'];
    return custom ? custom.join(', ') : '';
  }

  private setModalityClassifications(modality: Modality): void {
    const classification = this.form.get('classification') as FormGroup;
    this.classificationDimensions = [];
    for (const dimension of Object.keys(modality.classification || {})) {
      if (!(dimension in classification.controls)) {
        classification.addControl(dimension, new FormControl(this.file.classification[dimension] ?? []));
      }
      this.classificationDimensions.push({
        dimension,
        options: modality.classification[dimension].map(value => ({ value })),
      });
    }
  }

  getTranslation(key: string): string {
    return this.messages.dialog.get(key);
  }

  getModalityLabel(modalityId: string): string {
    const modality = this.modalities.get(modalityId);
    if (modality?.description) {
      return `${modalityId} - ${modality.description}`;
    }
    return modalityId;
  }

  versionHistory(): void {
    this.dialog.open(FileVersionDialog, {
      data: { file: this.file },
      disableClose: false,
      hasBackdrop: true,
      position: { right: '0' },
      panelClass: ['border-radius-0', 'padding-0'],
    });
  }

  getClassificationValue(): Record<string, string[]> {
    const currentDimensions = this.classificationDimensions.map(v => v.dimension);
    return this.classificationDimensions.length
      ? pick(this.form.get('classification').value, currentDimensions)
      : {};
  }

  saveHandler(): Observable<boolean> {
    const formValue = this.form.value; // or forms.get(FileInfoDialog).control.value
    formValue.modality = formValue.modality || null;
    formValue.classification = this.getClassificationValue();
    if (formValue.custom) {
      formValue.classification['Custom'] = formValue.custom.split(',').map(item => item.trim()).filter(item => item);
    }

    // FLYW-8905 we will persist classifications and tags serially until we get a transactional endpoint
    const serialRequests: Observable<unknown>[] = [];
    const logError = catchError(error => {
      this.logger.error(error);
      throw error;
    });

    if (this.form.get('name').dirty) {
      serialRequests.push(this.coreApi.files.move(this.file.file_id, {
        name: formValue.name,
        run_gear_rules: this.settings.runGearRulesOnMove ?? false,
      }).pipe(logError));
    }

    if (this.form.get('mimetype').dirty || this.form.get('type').dirty) {
      const metaPayload: Partial<File> = {};
      if (this.form.get('mimetype').dirty) {
        metaPayload.mimetype = formValue.mimetype;
      }
      if (this.form.get('type').dirty) {
        metaPayload.type = formValue.type;
      }
      serialRequests.push(this.coreApi.files.put(this.file.file_id, metaPayload).pipe(logError));
    }

    if (this.form.get('tags').dirty) {
      serialRequests.push(this.coreApi.files.tags.put(this.file.file_id, formValue.tags).pipe(logError));
    }

    if (formValue.modality !== this.file.modality || !isEqual(formValue.classification, this.file.classification)) {
      serialRequests.push(
        this.coreApi.files.classification(this.file.file_id, {
          modality: formValue.modality,
          replace: formValue.classification,
        }).pipe(
          tap(({ jobs_spawned: jobsSpawned }) => {
            if (jobsSpawned > 0) {
              const message = jobsSpawned === 1
                ? this.messages.dialog.get('jobs.oneQueued')
                : this.translate.interpolate(this.messages.dialog.get('jobs.manyQueued'), jobsSpawned);
              this.notifications.success(message, true);
            }
          }),
          logError,
        )
      );
    }

    if (this.customInfo.dirty) {
      serialRequests.push(defer(() => from(this.customInfo.saveFields())));
    }

    return concat(...serialRequests).pipe(
      startWith(undefined as void), // if empty, emit once
      last(), // emit only once the last request has finished
      map(() => serialRequests.length > 0),
    );
  }

  save(): void {
    if (!this.dirty || !this.valid) {
      return;
    }
    this.saveHandler().subscribe({
      next: (dirty: boolean) => {
        this.ref.close({ dirty });
      },
      error: () => {
        this.notifications.error('files.updateMetadata.error');
      },
    });
  }

  reset(): void {
    this.form.reset({
      name: this.file.name,
      mimetype: this.file.mimetype,
      modality: this.file.modality || '',
      custom: this.customClassification,
      tags: this.file.tags,
      type: this.file.type,
    });
    const classification = this.form.get('classification') as FormGroup;
    for (const [key, control] of Object.entries(classification.controls)) {
      control.reset(this.file.classification[key] ?? []);
    }
  }

  cancel(): void {
    this.ref.close({ dirty: false });
  }
}
